import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function ProjectsSection() {
  const { t } = useTranslation('common');
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  
  const projects = [
    {
      id: 1,
      title: t('projects.project1'),
      category: 'İç Mimari • Lüks Konut',
      location: t('projects.istanbul'),
      date: '2024',
      image: 'https://public.readdy.ai/ai/img_res/1c17c0d201288f15f8cc0c9a0448bd35.jpg',
      featured: true,
      details: {
        area: '450 m²',
        duration: '8 ay',
        client: 'Özel Müşteri',
        description: 'Modern lüks yaşam alanlarının organik formlarla buluştuğu bu projede, doğal malzemeler ve akıcı tasarım öğeleri kullanılarak benzersiz bir mekan yaratıldı.',
        features: ['Özel mobilya tasarımı', 'Akıllı ev sistemleri', 'Doğal taş uygulamaları', 'Özel aydınlatma tasarımı'],
        gallery: [
          'https://readdy.ai/api/search-image?query=luxury%20living%20room%20interior%20natural%20wood%20organic%20furniture%20warm%20lighting%20minimalist%20elegant%20design%20stone%20textures%20flowing%20elements&width=800&height=600&seq=project-1-gallery-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=modern%20luxury%20bedroom%20interior%20natural%20materials%20organic%20shapes%20warm%20ambient%20lighting%20elegant%20minimalist%20design&width=800&height=600&seq=project-1-gallery-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=luxury%20kitchen%20interior%20natural%20wood%20cabinets%20stone%20countertops%20modern%20minimalist%20design%20warm%20lighting&width=800&height=600&seq=project-1-gallery-3&orientation=landscape'
        ]
      }
    },
    {
      id: 2,
      title: t('projects.project2'),
      category: 'Kurumsal • Ofis Tasarımı',
      location: t('projects.dubai'),
      date: '2024',
      image: 'https://readdy.ai/api/search-image?query=modern%20corporate%20office%20interior%20with%20custom%20furniture%20natural%20materials%20minimalist%20design%20clean%20lines%20professional%20workspace%20with%20organic%20wooden%20elements&width=800&height=600&seq=project-2&orientation=landscape',
      featured: false,
      details: {
        area: '1200 m²',
        duration: '10 ay',
        client: 'Kurumsal Firma',
        description: 'Çalışan verimliliğini artıran, doğal malzemeler ve modern tasarımın bir araya geldiği yenilikçi ofis konsepti.',
        features: ['Açık ofis konsepti', 'Toplantı odaları', 'Dinlenme alanları', 'Akustik çözümler'],
        gallery: [
          'https://readdy.ai/api/search-image?query=modern%20office%20workspace%20natural%20wood%20furniture%20minimalist%20design%20professional%20environment%20clean%20lines&width=800&height=600&seq=project-2-gallery-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=corporate%20meeting%20room%20modern%20furniture%20natural%20materials%20minimalist%20design%20professional%20space&width=800&height=600&seq=project-2-gallery-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=office%20lounge%20area%20modern%20furniture%20natural%20materials%20comfortable%20workspace%20minimalist%20design&width=800&height=600&seq=project-2-gallery-3&orientation=landscape'
        ]
      }
    },
    {
      id: 3,
      title: t('projects.project3'),
      category: 'Ticari • Restoran',
      location: t('projects.london'),
      date: '2023',
      image: 'https://readdy.ai/api/search-image?query=elegant%20restaurant%20interior%20with%20custom%20wooden%20furniture%20natural%20stone%20walls%20warm%20ambient%20lighting%20sophisticated%20dining%20space%20with%20organic%20design%20elements&width=800&height=600&seq=project-3&orientation=landscape',
      featured: false,
      details: {
        area: '350 m²',
        duration: '6 ay',
        client: 'Restoran İşletmesi',
        description: 'Doğal malzemeler ve sıcak aydınlatmanın buluştuğu, misafirlere unutulmaz bir deneyim sunan restoran tasarımı.',
        features: ['Özel mutfak tasarımı', 'Atmosferik aydınlatma', 'Doğal taş duvarlar', 'Özel mobilya üretimi'],
        gallery: [
          'https://readdy.ai/api/search-image?query=restaurant%20dining%20area%20wooden%20furniture%20natural%20stone%20walls%20warm%20lighting%20elegant%20sophisticated%20space&width=800&height=600&seq=project-3-gallery-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=restaurant%20bar%20area%20custom%20wooden%20furniture%20natural%20materials%20warm%20ambient%20lighting%20modern%20design&width=800&height=600&seq=project-3-gallery-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=restaurant%20private%20dining%20room%20elegant%20furniture%20natural%20materials%20warm%20lighting%20sophisticated%20design&width=800&height=600&seq=project-3-gallery-3&orientation=landscape'
        ]
      }
    }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openProjectModal = (projectId: number) => {
    setSelectedProject(projectId);
    document.body.style.overflow = 'hidden';
  };

  const closeProjectModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  const currentProject = projects.find(p => p.id === selectedProject);

  return (
    <>
      <section id="projects" className="py-32 bg-[#F5F3F0]">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-light mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t('projects.title')}
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              {t('projects.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Featured Project */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              <div 
                onClick={() => openProjectModal(projects[0].id)}
                className="group relative overflow-hidden rounded-[20px] bg-white shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer h-[70vh]"
              >
                <div className="relative h-[calc(100%-100px)] overflow-hidden">
                  <img
                    src={projects[0].image}
                    alt={projects[0].title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500 flex items-center justify-center">
                    <span className="text-white text-lg font-light opacity-0 group-hover:opacity-100 transition-opacity duration-500 whitespace-nowrap">
                      {t('projects.viewDetails')}
                    </span>
                  </div>
                </div>
                <div className="p-6 h-[100px] flex flex-col justify-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">{projects[0].category}</p>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-1">{projects[0].title}</h3>
                  <p className="text-sm text-gray-500">{projects[0].location} • {projects[0].date}</p>
                </div>
              </div>
            </motion.div>

            {/* Small Projects */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              {projects.slice(1).map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  onClick={() => openProjectModal(project.id)}
                  className="group relative overflow-hidden rounded-[20px] bg-white shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer h-[calc(35vh-16px)]"
                >
                  <div className="relative h-[calc(100%-90px)] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500 flex items-center justify-center">
                      <span className="text-white text-base font-light opacity-0 group-hover:opacity-100 transition-opacity duration-500 whitespace-nowrap">
                        {t('projects.viewDetails')}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 h-[90px] flex flex-col justify-center">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{project.category}</p>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{project.title}</h3>
                    <p className="text-xs text-gray-500">{project.location} • {project.date}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-16"
          >
            <button
              onClick={() => scrollToSection('projects')}
              className="inline-block border-2 border-gray-900 text-gray-900 px-12 py-4 rounded-full text-sm font-light tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('Projeler')}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Project Detail Modal */}
      {selectedProject && currentProject && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={closeProjectModal}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeProjectModal}
              className="sticky top-4 right-4 float-right z-10 w-10 h-10 flex items-center justify-center bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Modal Content */}
            <div className="p-8 md:p-12">
              {/* Header */}
              <div className="mb-8">
                <p className="text-sm text-gray-500 uppercase tracking-wider mb-2">{currentProject.category}</p>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{currentProject.title}</h2>
                <p className="text-gray-600 text-lg">{currentProject.location} • {currentProject.date}</p>
              </div>

              {/* Main Image */}
              <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-8">
                <img
                  src={currentProject.image}
                  alt={currentProject.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Project Info Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-[#F5F3F0] rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Alan</p>
                  <p className="text-xl font-semibold text-gray-900">{currentProject.details.area}</p>
                </div>
                <div className="bg-[#F5F3F0] rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Süre</p>
                  <p className="text-xl font-semibold text-gray-900">{currentProject.details.duration}</p>
                </div>
                <div className="bg-[#F5F3F0] rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Müşteri</p>
                  <p className="text-xl font-semibold text-gray-900">{currentProject.details.client}</p>
                </div>
                <div className="bg-[#F5F3F0] rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Yıl</p>
                  <p className="text-xl font-semibold text-gray-900">{currentProject.date}</p>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Proje Hakkında</h3>
                <p className="text-gray-600 leading-relaxed text-lg">{currentProject.details.description}</p>
              </div>

              {/* Features */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Özellikler</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentProject.details.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-gray-900 rounded-full flex-shrink-0"></div>
                      <p className="text-gray-700">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gallery */}
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Galeri</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {currentProject.details.gallery.map((image, index) => (
                    <div key={index} className="w-full h-64 rounded-xl overflow-hidden group">
                      <img
                        src={image}
                        alt={`${currentProject.title} - ${index + 1}`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
